import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addToSitePlanCandidates,
  archiveProject,
  createProject,
  createStudy,
  deleteLayer,
  deleteProject,
  deleteRun,
  deleteStudy,
  getProject,
  getRun,
  getRunLayout,
  listLayers,
  listProjectEvents,
  listProjects,
  listRuns,
  listSitePlanCandidates,
  listStudies,
  getBoundary,
  patchProject,
  removeSitePlanCandidate,
  setFeatureClassification,
} from "../api/projects";
import type {
  CandidateType,
  Layer,
  Project,
  ProjectEventsResponse,
  ProjectsListResponse,
  RunLayout,
  SitePlanCandidate,
  WorkflowType,
} from "../types/projects";

const QK = {
  projects: (status?: string) => ["projects", { status }] as const,
  project: (id: string) => ["projects", id] as const,
  studies: (projectId: string) => ["projects", projectId, "studies"] as const,
  runs: (studyId: string) => ["studies", studyId, "runs"] as const,
  run: (runId: string) => ["runs", runId] as const,
  // Stage L.4.d — persistent layout payload, mutated optimistically by
  // useEditOps. Separate from the ``run`` query (Run row metadata) so
  // setQueryData on edits doesn't accidentally invalidate the run's
  // status / triggered_by / etc.
  runLayout: (runId: string) => ["run-layout", runId] as const,
  layers: (projectId: string) => ["projects", projectId, "files"] as const,
  boundary: (projectId: string) => ["projects", projectId, "boundary"] as const,
  // Stage 8.7
  sitePlanCandidates: (projectId: string, type?: CandidateType) =>
    ["projects", projectId, "site-plan-candidates", { type }] as const,
  events: (projectId: string) =>
    ["projects", projectId, "events"] as const,
};

export const projectsQueryKeys = QK;

export function useProjects(status?: string) {
  return useQuery<ProjectsListResponse>({
    queryKey: QK.projects(status),
    queryFn: () => listProjects({ status }),
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.project(id) : ["projects", "void"],
    queryFn: () => getProject(id!),
    enabled: !!id,
  });
}

export function useStudies(projectId: string | undefined) {
  return useQuery({
    queryKey: projectId ? QK.studies(projectId) : ["studies", "void"],
    queryFn: () => listStudies(projectId!),
    enabled: !!projectId,
  });
}

export function useRuns(studyId: string | undefined) {
  return useQuery({
    queryKey: studyId ? QK.runs(studyId) : ["runs", "void"],
    queryFn: () => listRuns(studyId!),
    enabled: !!studyId,
  });
}

export function useRun(runId: string | undefined) {
  return useQuery({
    queryKey: runId ? QK.run(runId) : ["runs", "void-single"],
    queryFn: () => getRun(runId!),
    enabled: !!runId,
  });
}

/**
 * Stage L.4.d: GET /api/runs/{run_id}/layout.json wrapped in React Query.
 * The cache for this key is the *canonical client-side state* —
 * ``useEditOps`` mutates it via ``setQueryData`` on every add/move/delete,
 * the layout viewer reads from it, the in-screen counter is derived from
 * ``data.trackers.length``. Do NOT invalidate after a successful op —
 * the optimistic update IS the new state.
 */
export function useRunLayout(runId: string | undefined) {
  return useQuery<RunLayout>({
    queryKey: runId ? QK.runLayout(runId) : ["run-layout", "void"],
    queryFn: () => getRunLayout(runId!),
    enabled: !!runId,
  });
}

export function useLayers(
  projectId: string | undefined,
  /**
   * Optional refetch interval in ms. The Files tab passes a non-zero
   * value while a classification BackgroundTask is in flight so the
   * row's badge updates without a hard refresh. Pass ``false`` (the
   * default) to disable polling.
   */
  refetchInterval: number | false = false,
) {
  return useQuery<Layer[]>({
    queryKey: projectId ? QK.layers(projectId) : ["files", "void"],
    queryFn: () => listLayers(projectId!),
    enabled: !!projectId,
    refetchInterval,
  });
}

export function useBoundary(projectId: string | undefined) {
  return useQuery<Layer | null>({
    queryKey: projectId ? QK.boundary(projectId) : ["boundary", "void"],
    queryFn: () => getBoundary(projectId!),
    enabled: !!projectId,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useCreateStudy(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; workflow_type?: WorkflowType }) =>
      createStudy(projectId, payload),
    onSuccess: () => {
      // The project detail query embeds studies, and the studies list
      // query is keyed separately — invalidate both so every consumer
      // sees the new row.
      void qc.invalidateQueries({ queryKey: QK.project(projectId) });
      void qc.invalidateQueries({ queryKey: QK.studies(projectId) });
    },
  });
}

export function useDeleteStudy(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteStudy,
    onSuccess: (_data, studyId) => {
      void qc.invalidateQueries({ queryKey: QK.project(projectId) });
      void qc.invalidateQueries({ queryKey: QK.studies(projectId) });
      qc.removeQueries({ queryKey: QK.runs(studyId) });
    },
  });
}

export function useDeleteRun(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteRun,
    onSuccess: (_data, runId) => {
      void qc.invalidateQueries({ queryKey: QK.runs(studyId) });
      qc.removeQueries({ queryKey: QK.run(runId) });
    },
  });
}

export function useArchiveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: archiveProject,
    onSuccess: (_data, projectId) => {
      void qc.invalidateQueries({ queryKey: ["projects"] });
      qc.removeQueries({ queryKey: QK.project(projectId) });
    },
  });
}

/**
 * Hard delete: full cascade (DB rows + S3 objects). Use from the
 * "Delete forever" confirm-modal CTA.
 */
export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteProject,
    onSuccess: (_data, projectId) => {
      void qc.invalidateQueries({ queryKey: ["projects"] });
      qc.removeQueries({ queryKey: QK.project(projectId) });
      qc.removeQueries({ queryKey: QK.layers(projectId) });
      qc.removeQueries({ queryKey: QK.boundary(projectId) });
      qc.removeQueries({ queryKey: QK.studies(projectId) });
    },
  });
}

/* ----- Stage 8.7: inline-edit name / description ----- */

/**
 * PATCH /api/projects/{id} — the Summary tab's inline-editable name +
 * description hooks bind to this. Invalidates the project query and
 * the projects list so the new name shows up on the list page too.
 */
export function usePatchProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      name?: string;
      description?: string;
      status?: string;
    }) => patchProject(projectId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.project(projectId) });
      void qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/* ----- Stage 8.7: site-plan candidates ----- */

export function useSitePlanCandidates(
  projectId: string | undefined,
  candidateType?: CandidateType,
) {
  return useQuery<SitePlanCandidate[]>({
    queryKey: projectId
      ? QK.sitePlanCandidates(projectId, candidateType)
      : ["site-plan-candidates", "void"],
    queryFn: () => listSitePlanCandidates(projectId!, candidateType),
    enabled: !!projectId,
  });
}

export function useAddToSitePlanCandidates(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      layer_id: string;
      feature_id?: string;
      candidate_type_override?: CandidateType;
    }) => addToSitePlanCandidates(projectId, payload),
    onSuccess: () => {
      // Refresh every type-filtered slice — keys all start with the
      // same prefix so a single invalidate covers them.
      void qc.invalidateQueries({
        queryKey: ["projects", projectId, "site-plan-candidates"],
      });
      void qc.invalidateQueries({ queryKey: QK.events(projectId) });
    },
  });
}

/**
 * Hard-delete a single uploaded layer. Invalidates the layers query
 * AND the site-plan-candidates query (DB cascade removes those rows
 * too) AND the boundary query (the layer may have been the active
 * boundary) AND the events query.
 */
export function useDeleteLayer(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (layerId: string) => deleteLayer(projectId, layerId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.layers(projectId) });
      void qc.invalidateQueries({
        queryKey: ["projects", projectId, "site-plan-candidates"],
      });
      void qc.invalidateQueries({ queryKey: QK.boundary(projectId) });
      void qc.invalidateQueries({ queryKey: QK.events(projectId) });
      void qc.invalidateQueries({ queryKey: QK.project(projectId) });
    },
  });
}

/**
 * Manually override the agent's per-feature classification for a single
 * feature inside a multi-feature geometry layer. Independent of the
 * Anthropic classifier — works with no API key. Invalidates the layers
 * query so the side-panel chip re-renders with the user's choice.
 */
export function useSetFeatureClassification(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      layerId,
      featureId,
      suggestedType,
    }: {
      layerId: string;
      featureId: string;
      suggestedType: CandidateType;
    }) =>
      setFeatureClassification(projectId, layerId, featureId, suggestedType),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.layers(projectId) });
      void qc.invalidateQueries({ queryKey: QK.events(projectId) });
    },
  });
}

export function useRemoveSitePlanCandidate(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (candidateId: string) =>
      removeSitePlanCandidate(projectId, candidateId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["projects", projectId, "site-plan-candidates"],
      });
      void qc.invalidateQueries({ queryKey: QK.events(projectId) });
    },
  });
}

/* ----- Stage 8.7: project events ----- */

export function useProjectEvents(
  projectId: string | undefined,
  limit: number = 20,
) {
  return useQuery<ProjectEventsResponse>({
    queryKey: projectId
      ? [...QK.events(projectId), { limit }]
      : ["events", "void"],
    queryFn: () => listProjectEvents(projectId!, { limit }),
    enabled: !!projectId,
  });
}

export type { Project };
